import {
  MecVariant,
  PriceGroup,
  PriceType,
} from '@adapters/bigcommerce/types/product';
import React from 'react';
import { usePrice } from '../usePrice';
import { PdpContext } from '@context/PdpContext';

export interface FormattedPrice {
  priceString: string | null;
  originalPriceString: string | null;
}

/** Min/max guest "original" tier for one variant price (matches single-variant branch order). */
function guestOriginalNumericSpan(
  guestPrice: PriceGroup
): { min: number; max: number } | null {
  if (guestPrice.basePrice?.value != null) {
    return { min: guestPrice.basePrice.value, max: guestPrice.basePrice.value };
  }
  if (
    guestPrice.baseLowPrice?.value != null &&
    guestPrice.baseHighPrice?.value != null
  ) {
    return {
      min: Math.min(
        guestPrice.baseLowPrice.value,
        guestPrice.baseHighPrice.value
      ),
      max: Math.max(
        guestPrice.baseLowPrice.value,
        guestPrice.baseHighPrice.value
      ),
    };
  }
  if (guestPrice.price?.value != null) {
    return { min: guestPrice.price.value, max: guestPrice.price.value };
  }
  if (
    guestPrice.lowPrice?.value != null &&
    guestPrice.highPrice?.value != null
  ) {
    return {
      min: Math.min(guestPrice.lowPrice.value, guestPrice.highPrice.value),
      max: Math.max(guestPrice.lowPrice.value, guestPrice.highPrice.value),
    };
  }
  return null;
}

function guestOriginalStringFromPriceGroup(
  guestPrice: PriceGroup,
  formatPrice: (value: number) => string | null
): string | null {
  const span = guestOriginalNumericSpan(guestPrice);
  if (!span) return null;
  if (span.min === span.max) {
    return formatPrice(span.min);
  }
  const low = formatPrice(span.min);
  const high = formatPrice(span.max);
  if (low == null || high == null) return null;
  return `${low} - ${high}`;
}

/** When no variant is selected, aggregate guest originals across variants of the selected colour only. */
function guestOriginalStringForColourScope(
  formatPrice: (value: number) => string | null,
  productVariants: MecVariant[] | undefined,
  selectedColourId: number,
  guestVariants:
    | Array<{ entityId: number; sku: string; price: PriceGroup }>
    | undefined
): string | null {
  if (!productVariants?.length || !guestVariants?.length) {
    return null;
  }

  const colourVariantIds = productVariants
    .filter((v) => v.colourId === selectedColourId)
    .map((v) => v.entityId)
    .filter((id): id is number => typeof id === 'number');

  const guestPrices = colourVariantIds
    .map((id) => guestVariants.find((gv) => gv.entityId === id)?.price)
    .filter((p): p is PriceGroup => p != null);

  const spans = guestPrices
    .map((p) => guestOriginalNumericSpan(p))
    .filter((s): s is { min: number; max: number } => s != null);

  if (!spans.length) {
    return null;
  }

  const minB = Math.min(...spans.map((s) => s.min));
  const maxB = Math.max(...spans.map((s) => s.max));
  if (minB === maxB) {
    return formatPrice(minB);
  }
  const low = formatPrice(minB);
  const high = formatPrice(maxB);
  if (low == null || high == null) return null;
  return `${low} - ${high}`;
}

/**
 * Derives formatted price strings during render (rerender-derived-state-no-effect).
 * Avoids extra render from useEffect and keeps UI in sync with context.
 */
const useFormattedPrice = (price: PriceGroup | null): FormattedPrice => {
  const formatPrice = usePrice();

  // Use context directly without throwing to allow usage outside PdpProvider
  const pdpContext = React.useContext(PdpContext);
  const customerGroupPrices = pdpContext?.customerGroupPrices;
  const selectedVariant = pdpContext?.selectedVariant;
  const selectedColour = pdpContext?.selectedColour;
  const productVariants = pdpContext?.variants;
  const shouldShowGuestPriceComparison =
    pdpContext?.shouldShowGuestPriceComparison;

  if (!price) {
    return { priceString: null, originalPriceString: null };
  }

  const priceString = price.price
    ? formatPrice(price.price.value)
    : `${formatPrice(price.lowPrice?.value)} - ${formatPrice(
        price.highPrice?.value
      )}`;
  let originalPriceString: string | null = null;

  const isSaleOrClearance = [PriceType.Clearance, PriceType.Sale].includes(
    price.priceType
  );

  if (shouldShowGuestPriceComparison && customerGroupPrices) {
    // Show Guest pricelist "New Price" as original for any non-guest group
    const colourScopedOriginal =
      !selectedVariant && selectedColour?.id != null
        ? guestOriginalStringForColourScope(
            formatPrice,
            productVariants,
            selectedColour.id,
            customerGroupPrices.guest?.variants
          )
        : null;

    const guestPrice = selectedVariant
      ? customerGroupPrices.guest?.variants.find(
          (v) => v.entityId === selectedVariant.entityId
        )?.price
      : customerGroupPrices.guest?.price;

    if (selectedVariant && guestPrice) {
      originalPriceString = guestOriginalStringFromPriceGroup(
        guestPrice,
        formatPrice
      );
    } else if (!selectedVariant) {
      originalPriceString =
        colourScopedOriginal ??
        (guestPrice
          ? guestOriginalStringFromPriceGroup(guestPrice, formatPrice)
          : null);
    }
  } else if (isSaleOrClearance) {
    originalPriceString = price.basePrice?.value
      ? formatPrice(price.basePrice.value)
      : `${formatPrice(price.baseLowPrice?.value)} - ${formatPrice(
          price.baseHighPrice?.value
        )}`;
  }

  return { priceString, originalPriceString };
};

export default useFormattedPrice;
